"use client";

import type { ConnectWalletResult } from "@1sat/connect";
import type { WalletInterface } from "@bsv/sdk";
import {
	createContext,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export type Brc100Source = "auto" | "web";

/**
 * Thrown when the BRC-100 substrate race finds no wallet (no extension,
 * no desktop on localhost, no XDM parent). Callers should treat this as
 * "show the picker / install hint", not a generic failure.
 */
export class NoBrc100WalletError extends Error {
	constructor() {
		super("No BRC-100 wallet detected");
		this.name = "NoBrc100WalletError";
	}
}

export interface OneSatContextValue {
	/** The live BRC-100 wallet, or null until connect() succeeds this session */
	wallet: WalletInterface | null;

	/** Whether a wallet is currently connected in this session */
	isConnected: boolean;

	/** Identity public key of the connected wallet (hex), or null */
	identityKey: string | null;

	/**
	 * Connect to the 1Sat web wallet via embedded CWI iframe at /wallet/cwi.
	 *
	 * Idempotent — returns the existing wallet if already connected,
	 * otherwise opens the iframe and waits for user authorization.
	 * Throws UserRejectedError / PopupBlockedError / PopupClosedError
	 * on failure.
	 */
	connect: () => Promise<WalletInterface>;

	/**
	 * Connect to any installed BRC-100 wallet via substrate auto-detect
	 * (`WalletClient('auto')`): browser extensions exposing `window.CWI`
	 * (e.g. Yours Wallet), desktop wallets on localhost (e.g. BSV-Desktop),
	 * XDM, React Native.
	 *
	 * Idempotent — returns the existing wallet if already connected.
	 * Returns null when no wallet is detected; callers should show the
	 * picker / install hint instead of a generic error.
	 */
	connectAuto: () => Promise<ConnectWalletResult | null>;

	/** Disconnect — detaches the iframe and clears wallet state. */
	disconnect: () => void;
}

const noWallet = (): Promise<WalletInterface> =>
	Promise.reject(new Error("OneSatProvider not mounted"));

const noAutoWallet = (): Promise<ConnectWalletResult | null> =>
	Promise.reject(new Error("OneSatProvider not mounted"));

const defaultValue: OneSatContextValue = {
	wallet: null,
	isConnected: false,
	identityKey: null,
	connect: noWallet,
	connectAuto: noAutoWallet,
	disconnect: () => {
		/* noop */
	},
};

export const OneSatContext = createContext<OneSatContextValue>(defaultValue);

// Note: consumers that need PopupBlockedError / PopupClosedError /
// UserRejectedError should import them directly from `@1sat/connect`.
// We deliberately do not re-export them here — re-exports would either
// (a) trigger biome's noExportedImports rule when paired with a runtime
// import, or (b) turn this file into a barrel (noBarrelFile).

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

interface OneSatProviderProps {
	children: ReactNode;
	/** Override the wallet host (default: https://1satwallet.com) */
	walletUrl?: string;
}

const DEFAULT_WALLET_URL = "https://1satwallet.com";

export function OneSatProvider({ children, walletUrl }: OneSatProviderProps) {
	const url = walletUrl ?? DEFAULT_WALLET_URL;

	const [wallet, setWallet] = useState<WalletInterface | null>(null);
	const [identityKey, setIdentityKey] = useState<string | null>(null);

	// Refs for the live connection — captured per connect() call so
	// disconnect() can tear down whatever connectWallet allocated.
	const disconnectRef = useRef<(() => void) | null>(null);
	// Deduplicate concurrent connect() calls so we don't open multiple iframes.
	const inFlightRef = useRef<Promise<WalletInterface> | null>(null);
	// Auto-detect race in flight (substrate probes, no iframe to dedupe).
	const autoInFlightRef = useRef<Promise<ConnectWalletResult | null> | null>(
		null
	);
	const walletRef = useRef<WalletInterface | null>(null);
	const identityKeyRef = useRef<string | null>(null);
	const sourceRef = useRef<Brc100Source | null>(null);

	const store = useCallback(
		(
			result: ConnectWalletResult,
			nextSource: Brc100Source,
			destroy: (() => void) | null
		): WalletInterface => {
			walletRef.current = result.wallet;
			identityKeyRef.current = result.identityKey;
			sourceRef.current = nextSource;
			disconnectRef.current = destroy;
			setWallet(result.wallet);
			setIdentityKey(result.identityKey);
			return result.wallet;
		},
		[]
	);

	/**
	 * Tear down whatever is live. Used by disconnect() and when switching
	 * sources: a desktop wallet must never be served to the web-wallet path
	 * or vice versa.
	 */
	const tearDown = useCallback(() => {
		disconnectRef.current?.();
		disconnectRef.current = null;
		walletRef.current = null;
		identityKeyRef.current = null;
		sourceRef.current = null;
		inFlightRef.current = null;
		autoInFlightRef.current = null;
		setWallet(null);
		setIdentityKey(null);
	}, []);

	const connect = useCallback(async (): Promise<WalletInterface> => {
		if (typeof window === "undefined") {
			throw new Error("connect() is only available in the browser");
		}
		if (walletRef.current) {
			if (sourceRef.current === "web") {
				return walletRef.current;
			}
			// Live wallet came from auto-detect — switch transports.
			tearDown();
		}
		if (inFlightRef.current) {
			return inFlightRef.current;
		}

		const attempt = (async (): Promise<WalletInterface> => {
			// This provider sits in the root layout, so load the wallet SDK only
			// when someone actually connects; every login/consent visitor would
			// otherwise download it for nothing.
			const { connectWallet } = await import("@1sat/connect");
			const result: ConnectWalletResult | null = await connectWallet({
				autoDetect: false,
				providers: [
					{
						type: "1sat",
						name: "1Sat Wallet",
						url,
					},
				],
			});
			if (!result) {
				throw new Error("Wallet connection cancelled");
			}
			return store(result, "web", result.disconnect);
		})();

		inFlightRef.current = attempt;
		try {
			return await attempt;
		} finally {
			inFlightRef.current = null;
		}
	}, [url, store, tearDown]);

	const connectAuto =
		useCallback(async (): Promise<ConnectWalletResult | null> => {
			if (typeof window === "undefined") {
				throw new Error("connectAuto() is only available in the browser");
			}
			if (walletRef.current && sourceRef.current === "auto") {
				return {
					wallet: walletRef.current,
					provider: "brc100",
					identityKey: identityKeyRef.current ?? "",
					disconnect: () => {
						/* substrate auto-detect needs no teardown */
					},
				};
			}
			if (walletRef.current) {
				// Live wallet came from the web bridge — switch transports.
				tearDown();
			}
			if (autoInFlightRef.current) {
				return autoInFlightRef.current;
			}

			// Pure substrate race — no configured providers. Probes window.CWI
			// (Yours and other extensions), localhost HTTP(S) (BSV-Desktop),
			// XDM and React Native. Resolves null when nothing is detected.
			const attempt = (async (): Promise<ConnectWalletResult | null> => {
				const { connectWallet } = await import("@1sat/connect");
				const result = await connectWallet({ autoDetect: true });
				if (!result) {
					return null;
				}
				store(result, "auto", null);
				return { ...result, provider: "brc100" };
			})();

			autoInFlightRef.current = attempt;
			try {
				return await attempt;
			} finally {
				autoInFlightRef.current = null;
			}
		}, [store, tearDown]);

	const disconnect = useCallback(() => {
		tearDown();
	}, [tearDown]);

	// Tear down the iframe when the provider unmounts.
	useEffect(() => {
		return () => {
			disconnectRef.current?.();
			disconnectRef.current = null;
			walletRef.current = null;
		};
	}, []);

	const value = useMemo<OneSatContextValue>(
		() => ({
			wallet,
			isConnected: wallet !== null,
			identityKey,
			connect,
			connectAuto,
			disconnect,
		}),
		[wallet, identityKey, connect, connectAuto, disconnect]
	);

	return (
		<OneSatContext.Provider value={value}>{children}</OneSatContext.Provider>
	);
}
